import { getCloudId } from '@databases/database-state';

import { getConfluenceService } from '@canvas/atlassian-service/confluence';
import { type Mention as ConfluenceMention } from '@canvas/atlassian-service/confluence/types';
import { getIdentity } from '@canvas/connection/identity';

import {
  type CheckContentPermissionsProps,
  type IdentifyNotificationEligibleUsersProps,
  type User
} from './types.ts';

export const checkContentPermissions = async ({
  accountIds,
  contentId
}: CheckContentPermissionsProps) => {
  const service = getConfluenceService();
  const permissions = await service.getContentPermissions({
    accountIds,
    contentId,
    cloudId: getCloudId(),
    resourceType: 'DATABASE'
  });

  return permissions;
};

export const identifyNotificationEligibleUsers = async ({
  contentId,
  mentions
}: IdentifyNotificationEligibleUsersProps) => {
  // For mention notification creation
  const usersToMention: ConfluenceMention[] = [];
  // For flag display
  const usersWithoutAccess: User[] = [];
  const usersWithAccess: User[] = [];

  const currentUserAccountId = (await getIdentity())?.accountId;
  const usersWithPermissionsInfo = await checkContentPermissions({
    accountIds: mentions.map((mention) => mention.mentionedUserAccountId),
    contentId
  });

  const mentionsWithPermissionsInfo = usersWithPermissionsInfo.map(
    (userWithPermissionsInfo) => {
      // Each `userWithPermissionsInfo` has a corresponding mention, lookup will be guaranteed
      const currentMention = mentions.find(
        (mention) => mention.mentionedUserAccountId === userWithPermissionsInfo.accountId
      );

      if (!currentMention) {
        throw new Error(`Mention not found`);
      }

      return {
        mentionLocalId: currentMention.mentionLocalId,
        mentionedUserAccountId: currentMention.mentionedUserAccountId,
        displayName: currentMention.displayName,
        profilePicture: userWithPermissionsInfo?.profilePicture?.path,
        permissions: {
          isSpaceRestricted: userWithPermissionsInfo.isSpaceRestricted,
          isParentDatabaseRestricted: userWithPermissionsInfo.isParentDatabaseRestricted,
          userHasAccessToContent: userWithPermissionsInfo.userHasAccessToContent
        }
      };
    }
  );

  for (const mention of mentionsWithPermissionsInfo) {
    if (mention.mentionedUserAccountId === currentUserAccountId) {
      continue;
    }

    usersToMention.push({
      mentionLocalId: mention.mentionLocalId,
      mentionedUserAccountId: mention.mentionedUserAccountId,
      notificationAction: mention.permissions.userHasAccessToContent
        ? 'NOTIFY'
        : 'DONT_NOTIFY'
    });

    if (!mention.permissions.userHasAccessToContent) {
      usersWithoutAccess.push({
        mentionLocalId: mention.mentionLocalId,
        mentionedUserAccountId: mention.mentionedUserAccountId,
        displayName: mention.displayName,
        profilePicture: mention.profilePicture,
        permissions: mention.permissions
      });
    } else {
      usersWithAccess.push({
        mentionLocalId: mention.mentionLocalId,
        mentionedUserAccountId: mention.mentionedUserAccountId,
        displayName: mention.displayName,
        profilePicture: mention.profilePicture,
        permissions: mention.permissions
      });
    }
  }

  return { usersToMention, usersWithoutAccess, usersWithAccess };
};
